import '#lit/components/base/icon/index.js';
import '../tooltip/index.js';
import type {TemplateResult} from 'lit';
import {html} from 'lit';
import {ref} from 'lit/directives/ref.js';
import {repeat} from 'lit/directives/repeat.js';
import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
  UpdateHandler,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
  extendArchitecture,
  mergeResults,
} from '#lit/components/mixins/immutable_component/index.js';
import type {
  VariantAtom,
  VariantsFor,
  SubModelFor,
} from '#lit/types/architecture.js';
import type {Point} from '#lit/lib/point.js';
import {nextTick} from '#lit/utils/index.js';
import {dispatchMovedEvent} from '#lit/utils/commands/journey_builder.js';
import type {ClassInfo} from 'lit/directives/class-map.js';
import {classMap} from 'lit/directives/class-map.js';
import {change} from '#lit/utils/commands/index.js';
import {option} from 'fp-ts';
import {pipe} from 'fp-ts/function';
import CanvasElement from '../canvas_element/index.js';
import type CanvasGroup from './index.js';
import {MAX_SUBNODES} from './constants.js';
import type CanvasNode from '../canvas_node/index.js';
import * as Super from '../canvas_element/architecture.js';
import {isCanvasNodeType} from '#lit/types/canvas.js';
import {journeysLocale} from '#lit/common/locale/journeys.js';

export type GroupRegistryItem<
  Node extends CanvasNode = CanvasNode,
  T = unknown,
> = {
  key: string;
  position: Point;
  order: number;
  ref?: Node;
  metadata: T;
};

export type Model = {
  registry: Map<string, GroupRegistryItem>;
  inputs: Set<HTMLElement>;
} & Super.Model;

type SubModel = SubModelFor<Model, Super.Model>;

export const Msg = makeVariants({
  addNode: Variant<GroupRegistryItem>()('add-node'),
  removeNode: Variant<GroupRegistryItem['key']>()('remove-node'),
  setRef: Variant<[string, CanvasNode]>()('set-ref'),
  addInput: Variant<HTMLElement>()('add-input'),
  removeInput: Variant<HTMLElement>()('remove-input'),
  ...Super.Msg,
});

export type Msg = VariantsFor<typeof Msg>;

const SuperProgram = extendArchitecture<Super.Model, Super.Msg, Model, Msg>(
  Super.main
);

const assignPositionStyles = (host: CanvasGroup) =>
  Object.assign(host.style, {
    top: host.model.position.y + 'px',
    left: host.model.position.x + 'px',
  });

// -- Init

function init(props: InitialProperties, cmd: Cmd): ImmutableResult<Model> {
  const inputs = (props.get('inputs') as HTMLElement[])?.length
    ? new Set<HTMLElement>(<HTMLElement[]>props.get('inputs'))
    : new Set<HTMLElement>();

  const model: SubModel = {
    registry: new Map<string, GroupRegistryItem>(),
    inputs,
  };

  return mergeResults(model, SuperProgram.init(props, cmd));
}

// -- Update

// FIXME(joshua): Make a utility for re-use in CanvasNode architecture.
function whenAttached(host: CanvasGroup) {
  host.$canvas?.cursor
    .on('drag', e => {
      if (host.isInPath(e.dragPath ?? [])) {
        host.select();
        return;
      }

      if (CanvasElement.isInPath(e.dragPath ?? [])) {
        host.deselect();
        return;
      }
    })
    .on('up', e => {
      if (e.ctx.hasDragged) return;

      pipe(
        e.composedPath ?? [],
        option.fromPredicate(host.isInPath.bind(host)),
        option.match(
          () => {
            CanvasElement.isInPath(e.composedPath ?? [])
              ? host.deselect()
              : (() => {
                  host.deselect();
                  host.$canvas!.deselectAll();
                  host.model.registry.forEach(item => item.ref?.deselect());
                })();
          },
          () => host.select()
        )
      );
    });

  assignPositionStyles(host);
}

export function reloadInputs(host: CanvasGroup) {
  nextTick(() => {
    host.setSocketPosition();
    dispatchMovedEvent(host);
    host.model.registry.forEach(item => {
      item.ref?.setSocketPosition();
      if (item.ref) dispatchMovedEvent(item.ref);
    });
  });
}

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-attached': {
      return mergeResults(
        [model, cmd(whenAttached)],
        SuperProgram.update(msg, model, cmd)
      );
    }

    case 'change-position': {
      return mergeResults(
        [model, cmd(assignPositionStyles)],
        SuperProgram.update(msg, model, cmd)
      );
    }

    case 'add-node': {
      model.registry.set(msg.value.key, msg.value);
      return [{...model}, cmd(reloadInputs)] as const;
    }

    case 'remove-node': {
      const targetItem = model.registry.get(msg.value);
      if (!targetItem) {
        console.error('Attempted to delete a removed ite,.');
        return model;
      }

      model.registry.forEach(item => {
        if (item.order > targetItem.order) item.order--;
      });

      model.registry.delete(msg.value);

      return [{...model}, cmd(reloadInputs)] as const;
    }

    case 'set-ref': {
      const [key, ref] = msg.value;
      if (!model.registry.has(key)) return model;

      model.registry.get(key)!.ref = ref;
      return {...model} as const;
    }

    case 'add-input': {
      model.inputs.add(msg.value);

      return [
        {
          ...model,
          inputs: new Set(model.inputs),
        },
        cmd(change),
      ] as const;
    }

    case 'remove-input': {
      model.inputs.delete(msg.value);

      return [
        {
          ...model,
          inputs: new Set(model.inputs),
        },
        cmd(change),
      ] as const;
    }
  }

  return SuperProgram.update(msg, model, cmd);
}

// -- View

export function refHandler<C extends CanvasGroup>(
  host: C,
  node: Element | undefined,
  cb?: (node: CanvasNode) => void
) {
  if (!node || !isCanvasNodeType(node) || !host.$canvas) return;

  if (!node.model.attached) host.$canvas.registerNode(node);
  if (!node.$group) node.$group = host;

  if (
    host.model.registry.has(node.key) &&
    !host.model.registry.get(node.key)!.ref
  ) {
    host.model.registry.get(node.key)!.ref = node;
    cb?.(node);
  }
}

function groupClasses(model: Model): ClassInfo {
  return {
    'canvas-group--selected': model.selected,
    'canvas-group--focused': model.focused,
    'canvas-group--connected': !!model.inputs.size,
  };
}

function viewNode(this: CanvasGroup, item: GroupRegistryItem) {
  return html`
    <fly-canvas-node
      ${ref(el => refHandler(this, el))}
      .key=${item.key}
      .position=${item.position}
      @destroyed=${() => this.handleUpdate(Msg.removeNode(item.key))}
    ></fly-canvas-node>
  `;
}

export function viewHeader(_host: CanvasGroup, template: TemplateResult) {
  return template;
}

export function viewGroup<
  M extends Model,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  N extends VariantAtom<any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  I extends GroupRegistryItem<any, any>,
  C extends CanvasGroup,
>({
  viewNode,
  viewHeader,
}: {
  viewNode: (
    this: C,
    item: I,
    model: M,
    handleUpdate: UpdateHandler<M, N>
  ) => TemplateResult;
  viewHeader?: (
    this: C,
    model: M,
    handleUpdate: UpdateHandler<M, N>
  ) => TemplateResult;
}) {
  return function (this: C, model: M, handleUpdate: UpdateHandler<M, N>) {
    return html`
      <div
        class="canvas-group ${classMap(groupClasses(model))}"
        @mousedown="${() => this.updateZIndex()}"
      >
        <!-- Group circle tracker icon -->
        <div class="canvas-group__tracker"></div>

        <!-- Group header -->
        <div class="canvas-group__header">
          ${viewHeader?.call(this, model, handleUpdate)}
          <slot name="header"></slot>
        </div>

        <!-- Group body -->
        <div class="canvas-group__nodes">
          ${repeat(
            Array.from(model.registry.values()).sort(
              (a, b) => a.order - b.order
            ),
            item => item.key,
            item => viewNode.call(this, item as I, model, handleUpdate)
          )}
          <div
            class="canvas-group__create"
            @click=${() => {
              if (exceedsSubnodeLimit(this.model.registry, MAX_SUBNODES)) {
                return;
              }
              this.onNewNodeClick();
            }}
          >
            ${renderCreateButton(this)}
          </div>
        </div>
      </div>
    `;
  };
}

function exceedsSubnodeLimit(
  nodeRegistry: Map<string, GroupRegistryItem>,
  maxSubNodes: number
) {
  return nodeRegistry.size >= maxSubNodes;
}

function renderCreateButton(node: CanvasGroup) {
  const button = html` <fly-icon icon="add"></fly-icon> `;
  if (exceedsSubnodeLimit(node.model.registry, MAX_SUBNODES)) {
    return html`
      <fly-tooltip
        content=${journeysLocale.en.journeysTable.canvasGroup.maxSubnodesTooltip(
          MAX_SUBNODES
        )}
      >
        ${button}
      </fly-tooltip>
    `;
  }
  return button;
}

// -- Program

export const main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view: viewGroup<Model, Msg, GroupRegistryItem, CanvasGroup>({viewNode}),
};
